// Pure evaluation of the buying rules against current page state
// (P1 gates: specs_20260707_quote_buying_rules.md · BP/SP matrix:
// specs_20260713_bp_sp_matrix.md). No I/O, no stored state — the warning set
// is re-derived from items + categories + config on every edit.
//
// items:             parseItemLine output ({ name, sp, bp, qty, condition, ... }, prices in pounds or null)
// itemCategories:    { [itemIndex]: mainCategory } from deal_item_categories
// config:            GET /api/buying-rules-config payload (money in cents, ratios 0-1)
// extras.itemSubCategories: { [itemIndex]: canonicalSub } from deal_item_categories (vision pass or manual)
// extras.pendingCompExclusions: { [itemIndex]: count } — comp exclusions newer than the item's latest pricing run
//
// Retired 2026-10-19 (Zsolt): guitar_amp_over_500 (stall gate), bp_under_floor /
// bp_over_cap (matrix floor/cap) and consignment_suggested. The matrix target and
// the consign/outright path still drive the item badges and the Why popover.
//
// Returns RuleHit[]: { ruleKey, severity: 'block'|'warn', itemIndex, itemName,
// message, spCents, bpCents, mainCategory, subCategory } — the snapshot fields
// feed deal_buying_rule_events.

import { CATEGORY_GROUP_ORDER } from '../../../utils/constants';
import { decideConsignment } from './consignmentDecision.js';
const CANONICAL_MAINS = new Set(CATEGORY_GROUP_ORDER);

export function evaluateBuyingRules(items, itemCategories, config, { itemSubCategories = {}, pendingCompExclusions = {} } = {}) {
    const hits = [];
    // Job-lot economics is a QUOTE property, not an item property: three £30
    // items are one £90 pickup and payout, so sp_under_50 gates on the quote's
    // total resale value (qty-weighted) and only then names each cheap line.
    const totalSpCents = items.reduce((sum, it) => {
        const sp = toCents(it.sp);
        return sp == null ? sum : sum + sp * (it.qty > 0 ? it.qty : 1);
    }, 0);
    items.forEach((item, itemIndex) => {
        // A non-canonical main (vision mislabel like "Hifi", pre-canonicaliser
        // rows) must behave as MISSING: taken at face value it passes the
        // mandatory-category gate while silently disabling every category-keyed
        // badge (GMROI, stance). Block visibly instead, naming the bad value.
        const rawMain = itemCategories[itemIndex] || null;
        const mainCategory = rawMain && CANONICAL_MAINS.has(rawMain) ? rawMain : null;
        const subCategory = itemSubCategories[itemIndex] || null;
        const spCents = toCents(item.sp);
        const bpCents = toCents(item.bp);
        const base = { itemIndex, itemName: item.name, spCents, bpCents, mainCategory, subCategory };

        if (!mainCategory) {
            hits.push({ ...base, ruleKey: 'missing_category', severity: 'block',
                message: rawMain
                    ? `Category "${rawMain}" isn't one of ours (vision mislabel) — pick the category before Quote Ready.`
                    : 'No main category — set one before Quote Ready.' });
        }
        if (spCents != null && spCents < config.sp_floor && totalSpCents < config.sp_floor) {
            hits.push({ ...base, ruleKey: 'sp_under_50', severity: 'warn', message: offerRuleOn(config)
                ? `SP £${pounds(spCents)} is under £${pounds(config.sp_floor)}: reject, unless this customer's quotes together come to £${pounds(config.sp_floor)} or more.`
                : `SP £${pounds(spCents)} is under £${pounds(config.sp_floor)} — job-lot economics only.` });
        }
        // Price predates operator comp exclusions — recalc or consciously offer anyway
        // (spec specs_20260713_comp_exclusions.md, workflow step 6).
        const stale = pendingCompExclusions[itemIndex];
        if (stale > 0) {
            hits.push({ ...base, ruleKey: 'comps_stale', severity: 'warn', message: `Price predates ${stale} of your comp exclusion${stale === 1 ? '' : 's'} — recalculate or offer anyway.` });
        }
    });
    return hits;
}

// True when the pricing engine's offer rule sets the buy price (GET /api/buying-rules-config).
export const offerRuleOn = (config) => config?.offer_rule_mode === 'on';

// Map a RuleHit to a deal_buying_rule_events row for POST /buying-rule-events.
export function toTelemetryEvent(hit, action) {
    return {
        item_index: hit.itemIndex,
        rule_key: hit.ruleKey,
        action,
        sp_cents: hit.spCents,
        bp_cents: hit.bpCents,
        main_category: hit.mainCategory,
        sub_category: hit.subCategory ?? null,
        stance: hit.stance ?? null,
        target_bp: hit.targetBp ?? null,
    };
}

// ---- BP/SP matrix (specs_20260713_bp_sp_matrix.md) ----

const BAND_LABELS = ['<£100', '£100–250', '£250–500', '£500–1000', '£1000+'];

// Lower-inclusive band edges: SP exactly £100 is the £100–250 band.
export function bandIndex(spCents, bandEdges) {
    let i = 0;
    while (i < bandEdges.length && spCents >= bandEdges[i]) i++;
    return i;
}

// Resolution chain (spec Q8): explicit canonical-sub entry → main-category default
// → global default. matchLevel is surfaced on the badge (Guard 2: fallback is
// always visible, never a silent main-as-sub match). ladder: null on an entry
// means "default ladder with this entry's stance".
export function resolveMatrixCell(subCategory, mainCategory, matrix) {
    const entry = (subCategory && matrix.by_sub?.[subCategory])
        ? { ...matrix.by_sub[subCategory], matchLevel: 'sub' }
        : (mainCategory && matrix.by_main?.[mainCategory])
            ? { ...matrix.by_main[mainCategory], matchLevel: 'main' }
            : { stance: matrix.default_stance, ladder: null, matchLevel: 'default' };
    return {
        stance: entry.stance,
        ladder: entry.ladder ?? matrix.default_ladder,
        dioDays: entry.dio_days ?? null,
        gmroi: entry.gmroi ?? null,
        why: entry.why ?? null,
        matchLevel: entry.matchLevel,
    };
}

// Unknown/absent condition → no modifier (0pp, treated as Excellent) but flagged
// so the badge shows "condition unknown" (user decision 2026-07-13).
export function conditionModifierPp(condition, modifiers) {
    if (condition == null || condition === '') return { pp: 0, known: false };
    const pp = modifiers[String(condition).toLowerCase()];
    return pp == null ? { pp: 0, known: false } : { pp, known: true };
}

// The item's condition-adjusted matrix target in basis points, with provenance.
// targetBp/band are null when SP is unknown (no band → no target).
export function resolveMatrixTarget(item, subCategory, mainCategory, config) {
    const matrix = config.bp_matrix;
    const spCents = toCents(item.sp);
    const cell = resolveMatrixCell(subCategory, mainCategory, matrix);
    const { pp, known } = conditionModifierPp(item.condition, matrix.condition_modifier_pp);
    const band = spCents != null && spCents > 0 ? bandIndex(spCents, matrix.band_edges_sp_cents) : null;
    const baseBp = band != null ? toBasisPoints(cell.ladder[band]) : null;
    return {
        stance: cell.stance,
        matchLevel: cell.matchLevel,
        dioDays: cell.dioDays,
        gmroi: cell.gmroi,
        why: cell.why,
        band,
        baseBp,
        conditionPp: pp,
        targetBp: baseBp != null ? baseBp + pp * 100 : null,
        conditionKnown: known,
    };
}

// {gmroi}/{dio} interpolation for the config-owned why copy (edited at monthly
// gate reviews — buyingRules.json commit + worker deploy, no frontend change).
export function fillWhyTemplate(template, { gmroi, dioDays }) {
    return template
        .replaceAll('{gmroi}', gmroi != null ? String(gmroi) : 'n/a')
        .replaceAll('{dio}', dioDays != null ? String(dioDays) : 'n/a');
}

// Everything the Why popover shows (user decision 2026-07-13: the UI explains
// WHY a target applies). Pure — the popover is a dumb renderer of this object.
// why resolution: per-sub override → matrix.why[stance], except an unmatched
// cell (matchLevel 'default') explains the fallback instead of fake economics.
//
// The numeric stance copy is only honest when the cell actually carries
// economics. An UNGRADED cell inherits a real stance and ladder but has no
// measured DIO/GMROI, and rendering the numeric template against those gaps
// printed the literal "n/a" — a fabricated claim about sell-through on top of a
// correct cap (reported 2026-07-17: "DJ Equipment · £500–1000 ... takes ~n/a
// days to sell"). Such cells get stance-matched copy with no numbers in it, the
// same discipline 'default' already had one level up. The guard is on economics
// PRESENCE, not matchLevel: 'Cymbals' is a by_sub row with a stance and no
// numbers, so a main-level-only guard would still have leaked n/a.
export function explainMatrixTarget(item, subCategory, mainCategory, config, signals = null) {
    const matrix = config.bp_matrix;
    const t = resolveMatrixTarget(item, subCategory, mainCategory, config);
    const spCents = toCents(item.sp);
    const bpCents = toCents(item.bp);
    const cellSub = subCategory || mainCategory || 'uncategorised';
    // ?? why.default covers deploy skew: this file ships with the frontend but
    // reads config served by the worker, so a frontend live ahead of its worker
    // would otherwise hit an undefined template and crash the whole panel. The
    // committed config is guarded worker-side (buyingRulesMatrix.test.js), and
    // why.default is itself honest numbers-free copy — so the fallback degrades
    // to slightly-generic wording, never to a fabricated figure.
    // A bespoke per-cell why always wins — dropping it on an ungraded cell would
    // silently ignore what a copy author wrote at a monthly review. Config-side
    // guard keeps such an override free of {dio}/{gmroi} unless the cell is graded.
    const hasEcon = t.gmroi != null && t.dioDays != null;
    const template = t.matchLevel === 'default'
        ? matrix.why.default
        : (t.why ?? (hasEcon
            ? matrix.why[t.stance]
            : (matrix.why[`ungraded_${t.stance}`] ?? matrix.why.default)));
    const decision = consignmentFor(t, spCents, bpCents, signals, matrix);
    const pathWhy = decision
        ? fillWhyTemplate(decision.path === 'consign' ? matrix.why.consign : matrix.why.outright, t)
        : null;
    return {
        cellLabel: t.band != null ? `${cellSub} · ${BAND_LABELS[t.band]}` : cellSub,
        stance: t.stance,
        matchLevel: t.matchLevel,
        bandLabel: t.band != null ? BAND_LABELS[t.band] : null,
        basePct: t.baseBp != null ? t.baseBp / 100 : null,
        conditionPp: t.conditionPp,
        conditionLabel: t.conditionKnown ? item.condition : 'condition unknown — no modifier',
        targetPct: t.targetBp != null ? t.targetBp / 100 : null,
        gmroi: t.gmroi,
        dioDays: t.dioDays,
        why: fillWhyTemplate(template, t),
        pathWhy,
    };
}

// Top-band index (£1000+ on current edges) — the two-path gate keys off the
// LAST ladder rung, derived from config so a re-tuned edge moves the badge
// path and popover together instead of leaving a hardcoded £1000 behind.
const topBand = (matrix) => matrix.band_edges_sp_cents.length;

// Direction checks for the badge tones. Careful cells flag BOTH ways around an exact percentage a whole-£
// offer usually can't hit (51% of £755 = £385.05): without grace, £385 warns
// under and £386 warns over — no compliant offer exists. So on careful, both
// nearest whole pounds count as on-target. Floors and caps stay strict: they
// are one-sided, so a compliant whole-£ offer always exists.
function directionFlags(bpCents, spCents, targetBp, stance) {
    if (stance === 'careful') {
        const targetPounds = (targetBp * spCents) / 1000000;
        return {
            under: bpCents < Math.floor(targetPounds) * 100,
            over: bpCents > Math.ceil(targetPounds) * 100,
        };
    }
    return {
        under: bpCents * 10000 < targetBp * spCents,
        over: bpCents * 10000 > targetBp * spCents,
    };
}

// Two-path gate (decideConsignment): never reject. The one place the badge path
// and the popover pathWhy ask "consign or outright?", so the two can never disagree. The SP side keys off the configured top band.
function consignmentFor(t, spCents, bpCents, signals, matrix) {
    return decideConsignment({ spCents, bpCents, spInTopBand: t.band === topBand(matrix), signals });
}

// Category economics for the item card display. subCategory is appended (not
// inserted) so the pre-matrix 3-arg callsite keeps working until the badge task.
export function getItemEconomics(item, mainCategory, config, subCategory = null, signals = null) {
    const spCents = toCents(item.sp);
    const bpCents = toCents(item.bp);
    const t = resolveMatrixTarget(item, subCategory, mainCategory, config);
    // GMROI at the matched cell's grain — the badge must agree with the why-copy
    // (Studio Monitors 1.06 must not render as Studio & Recording 3.6 on the same
    // card). gmroi_by_category is the family AVERAGE: shown only when the item
    // resolved to the default cell, and flagged so the badge can say so. A matched
    // cell without economics stays null — never a silent family figure.
    const gmroi = t.matchLevel === 'default'
        ? ((mainCategory && config.gmroi_by_category?.[mainCategory]) ?? null)
        : t.gmroi;
    const valid = spCents != null && bpCents != null && spCents > 0 && bpCents > 0;
    const consignmentPath = consignmentFor(t, spCents, bpCents, signals, config.bp_matrix)?.path ?? null;
    const dir = valid && t.targetBp != null
        ? directionFlags(bpCents, spCents, t.targetBp, t.stance)
        : { under: false, over: false };
    return {
        gmroi,
        gmroiGrain: gmroi == null ? null : (t.matchLevel === 'default' ? 'family' : 'cell'),
        dioDays: t.matchLevel === 'default' ? null : t.dioDays,
        stance: t.stance,
        matchLevel: t.matchLevel,
        band: t.band,
        conditionKnown: t.conditionKnown,
        targetRatio: t.targetBp != null ? t.targetBp / 10000 : null,
        currentRatio: valid ? bpCents / spCents : null,
        overTarget: dir.over,
        underTarget: dir.under,
        consignmentPath,
    };
}

const toCents = (v) => (v == null || v === '' || Number.isNaN(Number(v))) ? null : Math.round(Number(v) * 100);
const toBasisPoints = (ratio) => Math.round(ratio * 10000);
const pounds = (cents) => (cents % 100 === 0) ? String(cents / 100) : (cents / 100).toFixed(2);
